import { useState, useEffect } from "react";

import { Button, TextField, DatePicker, Modal, Typography } from "@king-ict/eupisi-ui/components";
import { ReactComponent as EditIcon } from "@king-ict/eupisi-ui/components/Icon/Edit.svg";
import { useValidation } from "@king-ict/eupisi-ui/hooks";
import { hrDate, hrDateWithTime } from "@king-ict/eupisi-ui/utils";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import { editEnvelope } from "../../api/nisooApi"
import StaticField from "../../components/StaticField";
import { Envelope, EnvelopeClassificationRequest, EnvelopeClassificationRequestValidationScheme } from "../../models/ProgramRequest";
import { useGetUserSessionQuery } from "../../queries/userQuery";
import styles from "./ProgramRequestDetails.module.scss";



type ProgramRequestEnvelopeProps = {
    name: string;
    envelope: Envelope;
    onEditModeChanged: () => void;
    isEditMode: boolean;
    onChange: (e: any) => void;
    isEditable: boolean;
}

export default function ProgramRequestEnvelopePanel({ name, envelope, onEditModeChanged, isEditMode, onChange, isEditable }: ProgramRequestEnvelopeProps): JSX.Element {
    const { t } = useTranslation();

    const createDefaultEnvelopeClassificationRequest = (): EnvelopeClassificationRequest => {
        return {
            envelopeId: 0,
            classificationMark: "",
            registryNumber: "",
            registrationDate: new Date()
        };
    };

    const [openCancelModal, setOpenCancelModal] = useState(false);
    const [openConfirmModal, setOpenConfirmModal] = useState(false);
    const [openSuccessModal, setOpenSuccessModal] = useState(false);
    const { data: userSession } = useGetUserSessionQuery();
    const [envelopeClassificationRequest, setEnvelopeClassificationRequest] = useState<EnvelopeClassificationRequest>(createDefaultEnvelopeClassificationRequest);

    const [errors, doValidations] = useValidation({
        data: envelopeClassificationRequest,
        validationSchema: EnvelopeClassificationRequestValidationScheme
    });



    const formattedDate = (inputDate?: Date, withTime?: boolean): string => {
        if (inputDate) {
            if (withTime === false) {
                return hrDate(inputDate);
            }
            return hrDateWithTime(inputDate);

        }
        return "";
    };

    useEffect(() => {
        if (isEditMode) {
            const newEnvelopeClassificationRequest: EnvelopeClassificationRequest = {
                envelopeId: envelope.id,
                classificationMark: envelope.classificationMark,
                registryNumber: envelope.registryNumber,
                registrationDate: envelope.registrationDate ? envelope.registrationDate : new Date()
            };
            setEnvelopeClassificationRequest(newEnvelopeClassificationRequest);
        }
    }, [isEditMode]);


    const cancelEdit = () => {
        setOpenCancelModal(true);
    };

    const saveEdit = () => {

        if (doValidations()) {
            setOpenConfirmModal(true);
        }
    };

    const handleCancelCancelModal = () => {
        setOpenCancelModal(false);
    };

    const handleConfirmedCancelModal = () => {
        setOpenCancelModal(false);
        onEditModeChanged();
    };

    const handleCancelConfirmModal = () => {
        setOpenConfirmModal(false);
    };

    const handleConfirmConfirmModal = async () => {

        await editEnvelope(envelopeClassificationRequest!).then(() => {
            setOpenSuccessModal(true);
        });
    };

    const handleCloseSuccessModal = () => {
        setOpenSuccessModal(false);
        setOpenConfirmModal(false);
        onEditModeChanged();

        const newEnvelope: Envelope = {
            ...envelope,
            classificationMark: envelopeClassificationRequest?.classificationMark ? envelopeClassificationRequest?.classificationMark : envelope.classificationMark,
            registryNumber: envelopeClassificationRequest?.registryNumber ? envelopeClassificationRequest?.registryNumber : envelope.registryNumber,
            registrationDate: envelopeClassificationRequest?.registrationDate
        };

        onChange({
            target: {
                name: name,
                value: newEnvelope
            }
        });

    };

    const handleClassificationMarkChange = (e: any) => {
        const classificationMark = e.target.value;

        if (envelopeClassificationRequest) {
            const newEnvelopeClassificationRequest: EnvelopeClassificationRequest = {
                ...envelopeClassificationRequest,
                classificationMark: classificationMark
            };
            setEnvelopeClassificationRequest(newEnvelopeClassificationRequest);
        }

    };

    const handleRegistryNumberChange = (e: any) => {
        const registryNumber = e.target.value;

        if (envelopeClassificationRequest) {
            const newEnvelopeClassificationRequest: EnvelopeClassificationRequest = {
                ...envelopeClassificationRequest,
                registryNumber: registryNumber
            };
            setEnvelopeClassificationRequest(newEnvelopeClassificationRequest);
        }

    };

    const handleRegistrationDateChange = (e: any) => {
        const registrationDate = e.target.value;

        if (envelopeClassificationRequest) {
            const newEnvelopeClassificationRequest: EnvelopeClassificationRequest = {
                ...envelopeClassificationRequest,
                registrationDate: registrationDate
            };
            setEnvelopeClassificationRequest(newEnvelopeClassificationRequest);
        }

    };


    return (
        <>
            <Row className={styles.dataRow}>
                <Col md={4}>
                    {isEditMode &&
                        <TextField
                            name={`${name}.classificationMark`}
                            label={t("common.classificationMark")}
                            value={envelopeClassificationRequest?.classificationMark}
                            onChange={handleClassificationMarkChange}
                            errorText={errors?.classificationMark}
                        />
                        ||
                        <StaticField
                            label={t("common.classificationMark")}
                            value={envelope.classificationMark}
                        />
                    }
                </Col>
                <Col md={4}>
                    {isEditMode &&
                        <TextField
                            name={`${name}.registryNumber`}
                            label={t("request.resolutionRegisterNumber")}
                            value={envelopeClassificationRequest?.registryNumber}
                            onChange={handleRegistryNumberChange}
                            errorText={errors?.registryNumber}
                        />
                        ||
                        <StaticField
                            label={t("request.resolutionRegisterNumber")}
                            value={envelope.registryNumber}
                        />
                    }
                </Col>
                <Col md={3}>
                    {isEditMode &&
                        <DatePicker
                            name={`${name}.registrationDate`}
                            label={t("request.committeeMembershipIssueDate")}
                            value={envelopeClassificationRequest?.registrationDate}
                            onChange={handleRegistrationDateChange}
                            isClearable={true}
                            minYear={new Date().getFullYear() - 25}
                            maxYear={new Date().getFullYear() + 25}
                            showTimePicker={false}
                            errorText={errors?.registrationDate}
                        />
                        ||
                        <StaticField
                            label={t("request.committeeMembershipIssueDate")}
                            value={formattedDate(envelope.registrationDate, false)}
                        />
                    }
                </Col>
                {userSession?.userId === envelope.receivedById && isEditable &&
                    <Col md={1}>
                        <EditIcon
                            className={styles.deleteButton}
                            title={t("request.classificationMarkRegistryNumberEditTooltip")}
                            onClick={onEditModeChanged}
                            visibility={isEditMode ? "hidden" : "visible"}
                        />
                    </Col>
                }
            </Row>
            <Row className={styles.dataRow}>
                <Col md={4}>
                    <StaticField
                        label={t("common.programRequestDate")}
                        value={formattedDate(envelope.envelopeDate)}
                    />
                </Col>
                <Col md={4}>
                    <StaticField
                        label={t("request.programRequestType")}
                        value={envelope.programRequestType}
                    />
                </Col>
            </Row>
            <Row className={styles.dataRow}>
                <Col md={4}>
                    <StaticField
                        label={t("request.contactAdministratorMail")}
                        value={envelope.receivedByContactMail}
                    />
                </Col>
                <Col md={4}>
                    <StaticField
                        label={t("request.contactAdministratorPhone")}
                        value={envelope.receivedByContactPhone}
                    />
                </Col>
                <Col md={4}>
                    <StaticField
                        label={t("request.administrator")}
                        value={envelope.receivedBy}
                    />
                </Col>
            </Row>
            {isEditMode &&
                <Row style={{ marginLeft: "-15px", marginRight: "-15px" }} className={"pt-2"}>
                    <Col md={12} className={styles.addDataButtonCol}>
                        <Button
                            outline
                            onClick={cancelEdit}
                        >
                            {t("common.cancel")}
                        </Button>
                        <Button
                            onClick={saveEdit}
                            className={styles.saveButton}
                        >
                            {t("common.save")}
                        </Button>
                    </Col>
                </Row>
            }

            <Modal
                type="confirm"
                open={openCancelModal}
                cancelText={t("common.noProperCase")}
                confirmText={t("common.yesCancel")}
                onClose={handleCancelCancelModal}
                onConfirm={handleConfirmedCancelModal}
            >
                <div>

                    <Typography component="h1" variant="largeBold">
                        {t("common.areYouSureCancelChanges")}
                    </Typography>
                    {t("common.areYouSureCancelChangesNote")}
                </div>
            </Modal>
            <Modal
                type="confirm"
                open={openConfirmModal}
                cancelText={t("common.cancel")}
                confirmText={t("common.yesSaveChanges")}
                onClose={handleCancelConfirmModal}
                onConfirm={handleConfirmConfirmModal}
            >
                <div>
                    <Typography component="h1" variant="largeBold">
                        {t("common.areYouSureSaveChanges")}
                    </Typography>
                </div>

            </Modal>
            <Modal
                type="message"
                open={openSuccessModal}
                confirmText={t("common.close")}
                onClose={handleCloseSuccessModal}
                onConfirm={handleCloseSuccessModal}
            >
                <div className={styles.modalMessageContentAlignCenter}>
                    <Typography component="h1" variant="largeBold">
                        {t("common.saveSucceed")}
                    </Typography>
                </div>
            </Modal>
        </>
    );
}